import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { DefaultView } from "@redotech/effect-api-shared/models/support-ticket/default-view";
import {
  AI_AGENT_ASSIGNEE_SENTINEL,
  filterGroupOverridesAiExclusion,
  filterGroupReferencesSpamTag,
  supportTicketFilterData,
  SupportTicketFilterNames,
} from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { SPAM_TAG } from "@redotech/redo-support-tags-common/conversation-tag-record";
import type { FilterGroup } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import type { ArrayStringFilterData } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { ArrayStringFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import {
  type NumberFilterData,
  NumberFilterOperator,
} from "@redotech/redo-views-common/advanced-filters/number-filter";

export const DEFAULT_VIEW_DISPLAY_NAMES: Record<DefaultView, string> = {
  [DefaultView.ALL]: "All tickets",
  [DefaultView.OPEN]: "Open",
  [DefaultView.ASSIGNED]: "Assigned to me",
  [DefaultView.MENTIONS]: "Mentions",
  [DefaultView.SENT]: "Sent",
  [DefaultView.DRAFTS]: "Drafts",
  [DefaultView.AI_CONVERSATIONS]: "AI conversations",
  [DefaultView.SPAM]: "Spam",
  [DefaultView.ARCHIVED]: "Archived",
};

export const DEFAULT_VIEW_DESCRIPTIONS: Record<DefaultView, string> = {
  [DefaultView.ALL]:
    "Every ticket across all statuses, including spam and AI conversations.",
  [DefaultView.OPEN]: "Open tickets that still need attention.",
  [DefaultView.ASSIGNED]: "Open tickets assigned to you.",
  [DefaultView.MENTIONS]: "Open tickets that mention you.",
  [DefaultView.SENT]: "Tickets with a sent message.",
  [DefaultView.DRAFTS]: "Tickets with unsent drafts.",
  [DefaultView.AI_CONVERSATIONS]: "Tickets assigned to the AI agent.",
  [DefaultView.SPAM]: "Tickets tagged as spam.",
  [DefaultView.ARCHIVED]: "Archived tickets.",
};

/** NONE_OF clause that hides spam-tagged tickets outside the Spam view. */
export const SPAM_TAG_EXCLUSION_FILTER: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.SUPPORT_TICKET_TAGS
  ] as ArrayStringFilterData),
  value: [SPAM_TAG],
  operator: ArrayStringFilterOperator.NONE_OF,
};

export const AI_ASSIGNEE_EXCLUSION_FILTER: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.ASSIGNEES
  ] as ArrayStringFilterData),
  value: [AI_AGENT_ASSIGNEE_SENTINEL],
  operator: ArrayStringFilterOperator.NONE_OF,
};

function appendFilter(
  group: FilterGroup | null,
  filter: ArrayStringFilterData,
): FilterGroup {
  if (!group) {
    return { operator: FilterOperator.AND, clauses: [filter] };
  }
  if (group.operator === FilterOperator.AND) {
    return { ...group, clauses: [...group.clauses, filter] };
  }
  return { operator: FilterOperator.AND, clauses: [group, filter] };
}

/** Adds the spam exclusion unless the group already references spam. */
export function withSpamTagExclusion(group: FilterGroup | null): FilterGroup {
  if (group && filterGroupReferencesSpamTag(group)) {
    return group;
  }
  return appendFilter(group, SPAM_TAG_EXCLUSION_FILTER);
}

export function withAiAssigneeExclusion(
  group: FilterGroup | null,
): FilterGroup {
  return group && filterGroupOverridesAiExclusion(group)
    ? group
    : appendFilter(group, AI_ASSIGNEE_EXCLUSION_FILTER);
}

export function withCustomViewDefaults(group: FilterGroup | null): FilterGroup {
  return withAiAssigneeExclusion(withSpamTagExclusion(group));
}

export function withInitialViewDefaults(
  view: DefaultView,
  group: FilterGroup,
): FilterGroup {
  const withSpam =
    view === DefaultView.ALL || view === DefaultView.SPAM
      ? group
      : withSpamTagExclusion(group);
  return view === DefaultView.ALL || view === DefaultView.AI_CONVERSATIONS
    ? withSpam
    : withAiAssigneeExclusion(withSpam);
}

const openStatusFilter: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

// New tickets saved as drafts have status="DRAFT"; existing ticket reply drafts
// have status="OPEN". The Drafts view must surface both.
const openOrDraftStatusFilter: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN", "DRAFT"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

export const OPEN_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [openStatusFilter, SPAM_TAG_EXCLUSION_FILTER],
};

const aiAssigneeAnyOfFilter: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.ASSIGNEES
  ] as ArrayStringFilterData),
  value: [AI_AGENT_ASSIGNEE_SENTINEL],
  operator: ArrayStringFilterOperator.ANY_OF,
};

export const AI_CONVERSATIONS_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [aiAssigneeAnyOfFilter, SPAM_TAG_EXCLUSION_FILTER],
};

export function buildAssignedViewFilterGroup(userId: string): FilterGroup {
  const assigneesFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.ASSIGNEES
    ] as ArrayStringFilterData),
    value: [MongoUserId.fromString(userId)],
    operator: ArrayStringFilterOperator.ANY_OF,
  };
  return {
    operator: FilterOperator.AND,
    clauses: [openStatusFilter, assigneesFilter, SPAM_TAG_EXCLUSION_FILTER],
  };
}

export function buildMentionsViewFilterGroup(userId: string): FilterGroup {
  const mentionsFilter: ArrayStringFilterData = {
    ...(supportTicketFilterData[
      SupportTicketFilterNames.MENTIONS
    ] as ArrayStringFilterData),
    value: [MongoUserId.fromString(userId)],
    operator: ArrayStringFilterOperator.ANY_OF,
  };
  return {
    operator: FilterOperator.AND,
    clauses: [openStatusFilter, mentionsFilter],
  };
}

const numSentMessagesFilter: NumberFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.NUM_SENT_MESSAGES
  ] as NumberFilterData),
  value: [0],
  operator: NumberFilterOperator.GREATER_THAN,
};

export const SENT_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [numSentMessagesFilter, SPAM_TAG_EXCLUSION_FILTER],
};

const numDraftsFilter: NumberFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.NUM_DRAFTS
  ] as NumberFilterData),
  value: [0],
  operator: NumberFilterOperator.GREATER_THAN,
};

export const DRAFTS_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    openOrDraftStatusFilter,
    numDraftsFilter,
    SPAM_TAG_EXCLUSION_FILTER,
  ],
};

export const SPAM_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    {
      ...(supportTicketFilterData[
        SupportTicketFilterNames.SUPPORT_TICKET_TAGS
      ] as ArrayStringFilterData),
      value: [SPAM_TAG],
      operator: ArrayStringFilterOperator.ANY_OF,
    },
  ],
};

export const ARCHIVED_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [
    {
      ...(supportTicketFilterData[
        SupportTicketFilterNames.STATUS
      ] as ArrayStringFilterData),
      value: ["ARCHIVED"],
      operator: ArrayStringFilterOperator.ANY_OF,
    },
    SPAM_TAG_EXCLUSION_FILTER,
  ],
};

const allStatusesFilter: ArrayStringFilterData = {
  ...(supportTicketFilterData[
    SupportTicketFilterNames.STATUS
  ] as ArrayStringFilterData),
  value: ["OPEN", "CLOSED", "IN_PROGRESS", "SNOOZED", "ARCHIVED", "DRAFT"],
  operator: ArrayStringFilterOperator.ANY_OF,
};

/** Shows all non-hidden, non-deleted tickets across every status. */
export const ALL_VIEW_FILTER_GROUP: FilterGroup = {
  operator: FilterOperator.AND,
  clauses: [allStatusesFilter],
};

/** Persistent scope for view behavior that survives filter edits. */
export const URL_VIEW_SCOPE_PARAM = "viewScope";

/** Scope whose reach is every ticket, spam and AI conversations included. */
export const ALL_VIEW_SCOPE = "all";

/** The scope marker a default view's URL should carry, if any. */
export function defaultViewScope(view: DefaultView): string | null {
  return view === DefaultView.ALL ? ALL_VIEW_SCOPE : null;
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export {
  DefaultView,
  isDefaultView,
} from "@redotech/effect-api-shared/models/support-ticket/default-view";
